/* ========================================
   Proadsy Vehicle Memory (V2 plan §09, 2026-10-15)
   Remembers ONE vehicle in this browser. No login,
   no garage, no VIN/PII — localStorage only.

   Canonical key:  proadsy_vehicle = {"year","make","model","label"}
   Mirrored key:   proadsy_ymmt    = {"year","make","model"}
                   (read by proadsy-garage.js + coll3 toolbar/grid)

   API:  window.ProadsyVehicle
     .get()               -> {year,make,model,label} | null
     .set({year,make,model})
     .clear()
     .label()             -> "2025 Toyota RAV4" | null
     .matches(metaText)   -> true | false | null (null = no vehicle saved
                             or no vehicle info in the product meta)
     .CHANGE_EVENT        -> 'proadsy:vehicle-changed' (document CustomEvent,
                             detail = vehicle | null)

   Chips: any element with [data-vehicle-chip] gets
   "Your vehicle: <label> · Change · Clear" rendered into it.
   ======================================== */
(function() {
  'use strict';

  var KEY = 'proadsy_vehicle';
  var LEGACY_KEY = 'proadsy_ymmt';
  var CHANGE_EVENT = 'proadsy:vehicle-changed';

  function normalize(v) {
    if (!v || !v.year || !v.make || !v.model) return null;
    return {
      year: String(v.year),
      make: String(v.make),
      model: String(v.model),
      label: v.label || (String(v.year) + ' ' + String(v.make) + ' ' + String(v.model))
    };
  }

  /* Legacy plain-string value ("2025 Toyota RAV4") written by older ymmt.js */
  function parseLegacyString(raw) {
    if (!raw || typeof raw !== 'string' || raw.charAt(0) === '{') return null;
    var parts = raw.trim().split(/\s+/);
    if (parts.length < 3 || !/^\d{4}$/.test(parts[0])) return null;
    return normalize({ year: parts[0], make: parts[1], model: parts.slice(2).join(' ') });
  }

  function get() {
    var v = null;
    try {
      var raw = localStorage.getItem(KEY);
      if (raw) {
        if (raw.charAt(0) === '{') v = normalize(JSON.parse(raw));
        else v = parseLegacyString(raw);
      }
      if (!v) {
        var legacy = localStorage.getItem(LEGACY_KEY);
        if (legacy) v = normalize(JSON.parse(legacy));
      }
    } catch (e) { v = null; }
    return v;
  }

  function dispatch(vehicle) {
    try {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: vehicle }));
    } catch (e) {}
  }

  function set(vehicle) {
    var v = normalize(vehicle);
    if (!v) return null;
    try {
      localStorage.setItem(KEY, JSON.stringify(v));
      /* keep the garage.js / collection-toolbar ecosystem in sync */
      localStorage.setItem(LEGACY_KEY, JSON.stringify({ year: v.year, make: v.make, model: v.model }));
    } catch (e) {}
    dispatch(v);
    renderChips();
    return v;
  }

  function clear() {
    try {
      localStorage.removeItem(KEY);
      localStorage.removeItem(LEGACY_KEY);
    } catch (e) {}
    dispatch(null);
    renderChips();
  }

  /* Does the product's vehicle display name cover the saved vehicle?
     metaText examples: "2022-2026 Toyota RAV4",
     "2023-2026 Toyota Sequoia / 2022-2026 Toyota Tundra" */
  function matches(metaText) {
    var v = get();
    if (!v) return null;
    var text = String(metaText || '').trim();
    if (!text || text === 'your vehicle') return null;
    var mm = (v.make + ' ' + v.model).toLowerCase();
    var yr = parseInt(v.year, 10);
    var segs = text.split('/');
    var nameHit = false;
    for (var i = 0; i < segs.length; i++) {
      var s = segs[i].toLowerCase();
      if (s.indexOf(mm) === -1) continue;
      nameHit = true;
      var range = s.match(/(\d{4})\s*[-–]\s*(\d{4})/);
      if (!range) return true; /* name matches, no year info to contradict */
      if (!isNaN(yr) && yr >= parseInt(range[1], 10) && yr <= parseInt(range[2], 10)) return true;
    }
    if (nameHit) return false; /* right nameplate, wrong year range */
    return false;
  }

  function filterRecommendations() {
    var items = document.querySelectorAll('[data-fitment-recommendation]');
    Array.prototype.forEach.call(items, function(item) {
      var hide = matches(item.getAttribute('data-vehicle-meta')) === false;
      item.hidden = hide;
      item.style.display = hide ? 'none' : '';
    });
    var groups = document.querySelectorAll('[data-fitment-recommendations][data-fitment-hide-empty]');
    Array.prototype.forEach.call(groups, function(group) {
      var recommendations = group.querySelectorAll('[data-fitment-recommendation]');
      var visible = 0;
      Array.prototype.forEach.call(recommendations, function(item) {
        if (!item.hidden) visible += 1;
      });
      var hide = recommendations.length > 0 && visible === 0;
      group.hidden = hide;
      group.style.display = hide ? 'none' : '';
    });
  }

  /* ---------- vehicle chip UI ---------- */
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function injectStyle() {
    if (document.getElementById('proadsy-vehicle-memory-css')) return;
    var style = document.createElement('style');
    style.id = 'proadsy-vehicle-memory-css';
    style.textContent = [
      '.pvchip{display:none;align-items:center;gap:8px;flex-wrap:wrap;font-size:12.5px;line-height:1.4;color:#16161A;',
      'padding:9px 14px;border:1px solid rgba(255,77,0,.35);border-radius:12px;background:rgba(255,77,0,.07);margin-bottom:14px}',
      '.pvchip.is-set{display:flex}',
      '.pvchip svg{flex:none;color:#FF4D00}',
      '.pvchip__txt{min-width:0}',
      '.pvchip__txt b{font-weight:700}',
      '.pvchip__act{margin-left:auto;display:flex;gap:12px;flex:none}',
      '.pvchip__act a,.pvchip__act button{font:inherit;font-size:12px;font-weight:700;color:#FF4D00;background:none;border:none;padding:0;cursor:pointer;text-decoration:underline;text-underline-offset:3px}',
      '.pvchip__act a:hover,.pvchip__act button:hover{color:#E04400}',
      '.pvchip--static{display:flex}'
    ].join('');
    document.head.appendChild(style);
  }

  function chipHTML(v) {
    return '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10l-2.7-3.6A2 2 0 0013.7 5H10.3a2 2 0 00-1.6.9L6 9.5 3.5 11.1A2 2 0 002 13v3c0 .6.4 1 1 1h2"/>' +
      '<circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>' +
      '<span class="pvchip__txt">Your vehicle: <b>' + esc(v.label) + '</b></span>' +
      '<span class="pvchip__act">' +
        '<a href="/#find-vehicle" data-pv-change>Change</a>' +
        '<button type="button" data-pv-clear>Clear</button>' +
      '</span>';
  }

  function renderChips() {
    filterRecommendations();
    var hosts = document.querySelectorAll('[data-vehicle-chip]');
    if (!hosts.length) return;
    injectStyle();
    var v = get();
    Array.prototype.forEach.call(hosts, function(host) {
      if (!v) {
        host.classList.remove('is-set');
        host.innerHTML = '';
        return;
      }
      host.classList.add('pvchip', 'is-set');
      host.setAttribute('role', 'status');
      host.innerHTML = chipHTML(v);
      var clearBtn = host.querySelector('[data-pv-clear]');
      if (clearBtn) clearBtn.addEventListener('click', function() { clear(); });
    });
  }

  window.ProadsyVehicle = {
    get: get,
    set: set,
    clear: clear,
    matches: matches,
    label: function() { var v = get(); return v ? v.label : null; },
    renderChips: renderChips,
    CHANGE_EVENT: CHANGE_EVENT
  };

  function boot() { renderChips(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Multi-tab sync */
  window.addEventListener('storage', function(e) {
    if (e.key !== KEY && e.key !== LEGACY_KEY) return;
    dispatch(get());
    renderChips();
  });
})();
